import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { EnvelopeOpen, ArrowsClockwise, ShieldCheck, SignOut } from "@phosphor-icons/react";

const POLL_MS = 5000;

/**
 * Post-registration landing page. The user is logged in but `email_verified=false`.
 * Cross-device flow: this page polls /api/auth/verification-status every 5s and
 * also re-checks on `visibilitychange`/`focus`. The user can click the email
 * link on ANY device — when the DB flag flips, this page proceeds.
 */
export default function PendingVerification() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [cooldown, setCooldown] = useState(0); // seconds remaining
  const [busy, setBusy] = useState(false);
  const verified = !!user?.email_verified;
  const intervalRef = useRef(null);

  const dashboardHref = user?.role === "seller" ? "/dashboard/seller"
    : user?.role === "admin" ? "/dashboard/admin"
    : "/dashboard/buyer";

  // Poll every 5s while tab is active.
  const checkStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/verification-status");
      if (data?.email_verified) {
        await refresh();
        nav(dashboardHref, { replace: true });
      }
    } catch { /* ignore transient errors */ }
  }, [refresh, nav, dashboardHref]);

  useEffect(() => {
    if (!user || verified) return;
    intervalRef.current = setInterval(checkStatus, POLL_MS);
    const onFocus = () => { if (!document.hidden) checkStatus(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, verified, checkStatus]);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Already verified? jump to dashboard
  useEffect(() => {
    if (verified) nav(dashboardHref, { replace: true });
  }, [verified, dashboardHref, nav]);

  if (!user) {
    // not logged in → bounce to login
    nav("/login", { replace: true });
    return null;
  }

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
      setCooldown(60);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Cooldown response → parse "Please wait Xs"
      if (e.response?.status === 429 && typeof detail === "string") {
        const match = detail.match(/(\d+)s/);
        if (match) setCooldown(parseInt(match[1], 10));
      }
      toast.error(formatApiError(detail) || "Couldn't send right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-mv-secondary text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Logo variant="full" height={36} />
        <div className="mt-10 bg-white text-mv-secondary rounded-3xl p-8 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.45)] space-y-5"
             data-testid="pending-verification-card">
          <div className="w-14 h-14 rounded-2xl bg-mv-primary/10 flex items-center justify-center">
            <EnvelopeOpen size={28} weight="duotone" className="text-mv-primary" />
          </div>
          <div>
            <h1 className="font-display font-black text-2xl">Verify your email to continue</h1>
            <p className="text-sm text-gray-500 mt-2">
              We sent a verification link to{" "}
              <span className="font-bold text-mv-secondary" data-testid="pending-email">
                {user.email}
              </span>.
              Tap the button in that email — you can open it on any device and this page will pick up
              automatically.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 leading-relaxed">
            <div className="font-bold uppercase tracking-[0.16em] text-[10px] text-mv-primary mb-1.5">
              While you wait
            </div>
            We&apos;re checking every few seconds. As soon as the email is verified — on this device or
            another — you&apos;ll be sent to your dashboard automatically.
          </div>

          <Button
            data-testid="resend-verification-btn"
            onClick={resend}
            disabled={cooldown > 0 || busy}
            className="w-full h-12 bg-mv-primary hover:bg-mv-primary-light text-white font-bold rounded-xl disabled:opacity-60"
          >
            <ArrowsClockwise size={18} weight="bold" className="mr-2" />
            {cooldown > 0 ? `Resend in ${cooldown}s` : (busy ? "Sending…" : "Resend verification email")}
          </Button>

          <div className="text-xs text-gray-500 leading-relaxed space-y-1">
            <div className="font-bold text-mv-secondary text-[13px]">Mistyped your email?</div>
            <div>
              You&apos;ll need to log out and register again with the correct address, or contact{" "}
              <a href="mailto:support@marketvyne.com" className="text-mv-primary font-bold">
                support@marketvyne.com
              </a>.
            </div>
          </div>

          <button
            data-testid="pending-logout-btn"
            onClick={async () => { await logout(); nav("/login", { replace: true }); }}
            className="w-full text-xs font-bold text-gray-400 hover:text-mv-secondary inline-flex items-center justify-center gap-1.5"
          >
            <SignOut size={14} /> Log out
          </button>
        </div>
        <div className="mt-5 text-xs text-white/60 inline-flex items-center gap-2">
          <ShieldCheck size={14} weight="duotone" className="text-mv-accent" />
          We never share your address. Tokens expire in 24 hours.
        </div>
      </div>
    </div>
  );
}
